/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useEffect, useRef} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import Field from '@amblique/king-living/app/components/field';
import useLoginFields from '@amblique/king-living/app/components/forms/useLoginFields';
import {AlertErrorIcon} from '@amblique/king-living/app/components/icons';
import PasswordlessLogin from '@amblique/king-living/app/components/passwordless-login';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Stack,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import SocialLogin from '@amblique/king-living/app/components/social-login';
import StandardLogin from '@amblique/king-living/app/components/standard-login';
import {EMAIL_REGEX} from '@amblique/king-living/app/utils/validation';

import WhySignUp from '@amblique/king-living/app/components/why-sign-up';

const LANDING_VIEW = 'landing';
const LOGIN_VIEW = 'login';

/**
 * KL-1037 — debounce (ms) between the shopper pausing typing and the existing-account lookup on the
 * landing step, so it routes to sign-in / registration without waiting for the field to blur.
 * Matches the 500ms timer used on checkout and the current kingliving.com.au.
 */
const EXISTING_ACCOUNT_CHECK_DEBOUNCE_MS = 500;

const LoginForm = ({
  currentView = LANDING_VIEW,
  customerData,
  email,
  emailInputRef,
  form,
  handleBackToLanding,
  handleForgotPasswordClick,
  handlePasswordlessLoginClick,
  handleResendLink,
  idps = [],
  isLoading = false,
  isPasswordlessEnabled = false,
  isPasswordlessEmailSent = false,
  isPasswordlessTokenInvalid = false,
  isSocialEnabled = false,
  setLoginType,
  submitForm,
}) => {
  const styles = useMultiStyleConfig('FormAuth', {});
  const fields = useLoginFields({form});
  const isLandingView = currentView === LANDING_VIEW;
  const isLoginView = currentView === LOGIN_VIEW;
  const customerName = customerData?.firstName || '';

  const renderHeader = () => {
    if (isLandingView) {
      return (
        <Stack className="login-header" {...styles.header}>
          <Text {...styles.title}>
            <FormattedMessage
              defaultMessage="Sign up or login"
              id="login_page.title"
            />
          </Text>
          <Text {...styles.description} {...styles.descriptionBlack}>
            <FormattedMessage
              defaultMessage="You will need to verify your email address"
              id="login_page.landing.description"
            />
          </Text>
        </Stack>
      );
    }

    // Don't show welcome back message when passwordless email is sent
    if (isLoginView && customerName && !isPasswordlessEmailSent) {
      return (
        <Stack className="login-header" {...styles.header}>
          <Text {...styles.title}>
            <FormattedMessage
              defaultMessage="Sign up or login"
              id="login_page.title"
            />
          </Text>
          <Text {...styles.subtitle}>
            <FormattedMessage
              defaultMessage="Welcome back, {name}!"
              id="login_page.welcome_back"
              values={{name: customerName}}
            />
          </Text>
          <Text {...styles.descriptionText}>
            <FormattedMessage
              defaultMessage="It looks like you already have an account. Sign in for faster checkout and to save this order to your history."
              id="login_page.login.existing_account_description"
            />
          </Text>
        </Stack>
      );
    }

    // When passwordless email is sent, only show title (announcement is shown in form body)
    if (isPasswordlessEmailSent) {
      return (
        <Stack className="login-header" {...styles.header}>
          <Text {...styles.title}>
            <FormattedMessage
              defaultMessage="Sign up or login"
              id="login_page.title"
            />
          </Text>
        </Stack>
      );
    }

    return (
      <Stack {...styles.header}>
        <Text {...styles.title}>
          <FormattedMessage
            defaultMessage="Sign up or login"
            id="login_page.title"
          />
        </Text>
        <Text {...styles.description} {...styles.descriptionBlack}>
          <FormattedMessage
            defaultMessage="You will need to verify your email address"
            id="login_page.login.description"
          />
        </Text>
      </Stack>
    );
  };

  const handleLandingSubmit = () => {
    if (!isLoading && !form.formState.isSubmitting) {
      // clearErrors: manual 'global' errors (e.g. invalid token) block handleSubmit onValid in RHF v7
      form.clearErrors();
      form.handleSubmit(submitForm)();
    }
  };

  // KL-1037 — auto-run the existing-account lookup ~500ms after the shopper stops typing a valid
  // email on the landing step, so it routes to sign-in / registration WITHOUT having to blur the
  // field or press Enter (parity with checkout + kingliving.com.au). Blur / Enter still trigger it
  // immediately. Guards: only on the landing step, not while a lookup is in flight, only for a
  // well-formed email (shared EMAIL_REGEX — so we don't surface a validation error mid-typing), and
  // skipped when the email is unchanged from the last attempt so we don't re-submit the same value.
  // Each keystroke resets the timer (cleanup), giving a true debounce.
  const landingEmailValue = form.watch('email');
  const lastAttemptedEmailRef = useRef('');

  useEffect(() => {
    if (!isLandingView || isLoading || form.formState.isSubmitting) return undefined;

    const email = (landingEmailValue || '').trim();

    if (!EMAIL_REGEX.test(email)) return undefined;

    if (email === lastAttemptedEmailRef.current) return undefined;

    const timer = setTimeout(() => {
      lastAttemptedEmailRef.current = email;
      handleLandingSubmit();
    }, EXISTING_ACCOUNT_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingEmailValue, isLandingView, isLoading, form.formState.isSubmitting]);

  const renderLandingForm = () => (
    <Stack className="landing-login-form-stack" gap={0}>
      <Field
        {...fields.email}
        inputProps={{
          ref: (e) => {
            if (emailInputRef) {
              emailInputRef.current = e;
            }
          },
          onBlur: handleLandingSubmit,
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleLandingSubmit();
            }
          },
        }}
      />
      {isSocialEnabled && idps.length > 0 && (
        <Stack {...styles.buttonContainer}>
          <Text {...styles.divider}>
            <FormattedMessage
              defaultMessage="Or continue with"
              id="login_page.or_continue_with"
            />
          </Text>
          <SocialLogin form={form} idps={idps} />
        </Stack>
      )}
    </Stack>
  );

  const renderLoginForm = () => {
    if (isPasswordlessEnabled) {
      return (
        <PasswordlessLogin
          email={email}
          form={form}
          handleBackToLanding={handleBackToLanding}
          handleForgotPasswordClick={handleForgotPasswordClick}
          handlePasswordlessLoginClick={handlePasswordlessLoginClick}
          handleResendLink={handleResendLink}
          idps={idps}
          isPasswordlessEmailSent={isPasswordlessEmailSent}
          isPasswordlessTokenInvalid={isPasswordlessTokenInvalid}
          isSocialEnabled={isSocialEnabled}
          setLoginType={setLoginType}
        />
      );
    }

    return (
      <StandardLogin
        email={email}
        form={form}
        handleBackToLanding={handleBackToLanding}
        handleForgotPasswordClick={handleForgotPasswordClick}
        idps={idps}
        isSocialEnabled={isSocialEnabled}
      />
    );
  };

  return (
    <Fragment>
      {renderHeader()}
      <form
        data-testid="sf-auth-modal-form"
        id="login-form"
        onSubmit={form.handleSubmit(submitForm)}
      >
        {form.formState.errors?.global && (
          <Alert
            className="form-alert"
            marginBottom={8}
            status="error"
            variant="error"
          >
            <AlertIcon as={AlertErrorIcon} />
            <Stack flex="1" spacing={1}>
              <AlertTitle>{form.formState.errors.global.message}</AlertTitle>
            </Stack>
          </Alert>
        )}
        <Stack className="login-form-stack">
          {isLandingView && renderLandingForm()}
          {isLoginView && renderLoginForm()}
        </Stack>
      </form>
      {isLandingView && <WhySignUp />}
    </Fragment>
  );
};

LoginForm.propTypes = {
  currentView: PropTypes.oneOf([LANDING_VIEW, LOGIN_VIEW]),
  customerData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
  }),
  email: PropTypes.string,
  emailInputRef: PropTypes.object,
  form: PropTypes.object,
  handleBackToLanding: PropTypes.func,
  handleForgotPasswordClick: PropTypes.func,
  handlePasswordlessLoginClick: PropTypes.func,
  handleResendLink: PropTypes.func,
  idps: PropTypes.arrayOf(PropTypes.string),
  isLoading: PropTypes.bool,
  isPasswordlessEnabled: PropTypes.bool,
  isPasswordlessEmailSent: PropTypes.bool,
  isPasswordlessTokenInvalid: PropTypes.bool,
  isSocialEnabled: PropTypes.bool,
  setLoginType: PropTypes.func,
  submitForm: PropTypes.func,
};

export default LoginForm;
